import {
  colRowToPx,
  hexPath,
  type PlayerId,
  parseHexId,
  type WarchestState,
} from "@cloudnest/warchest-engine";
import { PLAYER_COLORS, WarchestUnitSymbolInBoard } from "./WarchestUnitSymbol";

interface WarchestBoardProps {
  readonly gameState: WarchestState;
  readonly legalTargets: readonly string[];
  readonly selectedHex: string | null;
  readonly onHexClick: (hex: string) => void;
  readonly disabled?: boolean;
}

const HEX_SIZE = 28;
const PAD = HEX_SIZE * 1.1;

const STARTING_FACTION_COLOR: Record<PlayerId, string> = {
  P1: "var(--cn-warchest-raven)",
  P2: "var(--cn-warchest-wolf)",
};

function locationKnotwork(
  cx: number,
  cy: number,
  size: number,
  color: string,
  testId: string,
) {
  const r = size * 0.45;
  const tr = r * 0.7;
  const verts: Array<readonly [number, number]> = [];
  for (let i = 0; i < 6; i += 1) {
    const a = ((i * 60 - 90) * Math.PI) / 180;
    verts.push([cx + tr * Math.cos(a), cy + tr * Math.sin(a)]);
  }
  const tri = (a: number, b: number, c: number) =>
    `${verts[a]?.[0].toFixed(1)},${verts[a]?.[1].toFixed(1)} ${verts[b]?.[0].toFixed(1)},${verts[b]?.[1].toFixed(1)} ${verts[c]?.[0].toFixed(1)},${verts[c]?.[1].toFixed(1)}`;
  return (
    <g pointerEvents="none" data-testid={testId}>
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill="none"
        stroke={color}
        strokeWidth={1.2}
        opacity={0.55}
      />
      <polygon
        points={tri(0, 2, 4)}
        fill="none"
        stroke={color}
        strokeWidth={0.9}
        opacity={0.5}
      />
      <polygon
        points={tri(1, 3, 5)}
        fill="none"
        stroke={color}
        strokeWidth={0.9}
        opacity={0.5}
      />
      <circle cx={cx} cy={cy} r={r * 0.18} fill={color} opacity={0.75} />
    </g>
  );
}

function controlMarker(
  cx: number,
  cy: number,
  size: number,
  color: string,
  testId: string,
) {
  const ox = cx + size * 0.55;
  const oy = cy + size * 0.55;
  const r = size * 0.22;
  return (
    <g pointerEvents="none" data-testid={testId}>
      <circle
        cx={ox}
        cy={oy}
        r={r}
        fill={color}
        stroke="var(--cn-warchest-card-bg)"
        strokeWidth={1.3}
      />
      <circle
        cx={ox}
        cy={oy}
        r={r * 0.5}
        fill="var(--cn-warchest-card-bg)"
        opacity={0.4}
      />
    </g>
  );
}

export function WarchestBoard({
  gameState,
  legalTargets,
  selectedHex,
  onHexClick,
  disabled = false,
}: WarchestBoardProps) {
  const boardDef = gameState.config.boards[gameState.boardId];
  if (!boardDef) return null;

  const positioned = boardDef.hexes.map((key) => {
    const [col, row] = parseHexId(key);
    const [x, y] = colRowToPx(col, row, HEX_SIZE);
    return { key, col, row, x, y };
  });
  const xs = positioned.map((p) => p.x);
  const ys = positioned.map((p) => p.y);
  const minX = Math.min(...xs) - PAD;
  const minY = Math.min(...ys) - PAD;
  const maxX = Math.max(...xs) + PAD;
  const maxY = Math.max(...ys) + PAD;
  const width = maxX - minX;
  const height = maxY - minY;

  const legalTargetSet = new Set(disabled ? [] : legalTargets);
  const locationSet = new Set(boardDef.locations);

  return (
    <svg
      width="100%"
      viewBox={`${minX} ${minY} ${width} ${height}`}
      preserveAspectRatio="xMidYMid meet"
      className="mx-auto block sm:max-w-[560px]"
      style={{
        opacity: disabled ? 0.6 : 1,
      }}
      data-testid="warchest-board"
    >
      <rect
        x={minX}
        y={minY}
        width={width}
        height={height}
        fill="var(--cn-warchest-bg)"
        rx={20}
      />
      {positioned.map(({ key, x, y }) => {
        const cell = gameState.board[key];
        const location = gameState.locations[key];
        const isLegal = legalTargetSet.has(key);
        const isSelected = selectedHex === key;
        const controlledBy = location?.controlledBy ?? null;
        const isLocation = locationSet.has(key);
        const isStartingLocation =
          isLocation &&
          (boardDef.startLocations.P1?.includes(key) ||
            boardDef.startLocations.P2?.includes(key));
        const startingFaction = boardDef.startLocations.P1?.includes(key)
          ? "P1"
          : boardDef.startLocations.P2?.includes(key)
            ? "P2"
            : null;
        const isUnknownUnit = cell?.unitType === "UNKNOWN";

        const knotColor =
          isStartingLocation && startingFaction
            ? STARTING_FACTION_COLOR[startingFaction]
            : "var(--cn-warchest-raven)";

        return (
          <g
            key={key}
            onClick={() => !disabled && onHexClick(key)}
            style={{ cursor: disabled ? "default" : "pointer" }}
            data-testid={`warchest-hex-${key}`}
            data-player={cell?.owner ?? undefined}
            data-unit-type={cell?.unitType ?? undefined}
            data-controlled-by={controlledBy ?? undefined}
            data-legal-target={isLegal ? "true" : undefined}
            data-selected={isSelected ? "true" : undefined}
          >
            <path
              d={hexPath(x, y, HEX_SIZE)}
              fill="var(--cn-warchest-hex-fill)"
              stroke="var(--cn-warchest-hex-border)"
              strokeWidth={0.75}
            />
            {isLegal && !cell && (
              <path
                d={hexPath(x, y, HEX_SIZE)}
                fill="var(--cn-warchest-valid-deploy)"
                stroke="var(--cn-warchest-raven)"
                strokeWidth={2}
              />
            )}
            {isLocation &&
              locationKnotwork(
                x,
                y,
                HEX_SIZE,
                knotColor,
                `warchest-location-${key}`,
              )}
            {controlledBy &&
              controlMarker(
                x,
                y,
                HEX_SIZE,
                STARTING_FACTION_COLOR[controlledBy],
                `warchest-control-${key}`,
              )}
            {isSelected && !cell && (
              <circle
                cx={x}
                cy={y}
                r={HEX_SIZE * 0.85}
                fill="none"
                stroke="var(--cn-warchest-selected)"
                strokeWidth={2.5}
                pointerEvents="none"
              />
            )}
            {cell && !isUnknownUnit && (
              <>
                {isSelected && (
                  <circle
                    cx={x}
                    cy={y}
                    r={HEX_SIZE * 0.7}
                    fill="none"
                    stroke="var(--cn-warchest-selected)"
                    strokeWidth={2.5}
                  />
                )}
                {isLegal && (
                  <circle
                    cx={x}
                    cy={y}
                    r={HEX_SIZE * 0.7}
                    fill="none"
                    stroke="var(--cn-warchest-attack)"
                    strokeWidth={1.8}
                    strokeDasharray="3 2.5"
                  />
                )}
                <WarchestUnitSymbolInBoard
                  unitType={cell.unitType}
                  owner={cell.owner}
                  cx={x}
                  cy={y}
                />
                {cell.stackSize > 1 && (
                  <g pointerEvents="none">
                    <circle
                      cx={x + HEX_SIZE * 0.6}
                      cy={y - HEX_SIZE * 0.6}
                      r={8}
                      fill="var(--cn-warchest-card-bg)"
                      stroke="var(--cn-warchest-hex-edge)"
                      strokeWidth={0.6}
                    />
                    <text
                      x={x + HEX_SIZE * 0.6}
                      y={y - HEX_SIZE * 0.6 + 3}
                      textAnchor="middle"
                      fontSize={10}
                      fontWeight="700"
                      fill="var(--cn-warchest-text)"
                    >
                      {cell.stackSize}
                    </text>
                  </g>
                )}
              </>
            )}
            {cell && isUnknownUnit && (
              <g pointerEvents="none">
                <circle
                  cx={x}
                  cy={y}
                  r={HEX_SIZE * 0.55}
                  fill={PLAYER_COLORS[cell.owner]}
                  opacity={0.5}
                />
                <circle
                  cx={x}
                  cy={y}
                  r={HEX_SIZE * 0.55}
                  fill="none"
                  stroke="var(--cn-warchest-coin-rim-stroke)"
                  strokeWidth={1}
                />
                <text
                  x={x}
                  y={y + 5}
                  textAnchor="middle"
                  fontSize={16}
                  fontWeight="700"
                  fill="var(--cn-warchest-card-bg)"
                  opacity={0.85}
                >
                  ?
                </text>
              </g>
            )}
          </g>
        );
      })}
    </svg>
  );
}
